import { useApolloClient } from "@apollo/client";
import { useCallback, useRef, useState } from "react";
import { TraceLocationCertificates } from "../graphql/queries";
import { Certificate, LocationCertificatesMap } from "../types";

export function useLocationCertificates() {
    const client = useApolloClient();
    const [locationCertificates, setLocationCertificates] = useState<LocationCertificatesMap>({});
    const [loading, setLoading] = useState<boolean>(false);
    const fetchedEventIds = useRef<Set<string>>(new Set());
    const pendingEventIds = useRef<Set<string>>(new Set());

    const fetchLocationCertificates = useCallback(async (eventIds: string[]) => {
        const newEventIds = eventIds.filter(
            id => !fetchedEventIds.current.has(id) && !pendingEventIds.current.has(id)
        );

        if (newEventIds.length === 0) return;

        newEventIds.forEach(id => pendingEventIds.current.add(id));
        setLoading(true);

        const maxRetries = 2;
        let attempt = 0;
        let success = false;

        while (attempt < maxRetries && !success) {
            try {
                const { data } = await client.query({
                    query: TraceLocationCertificates,
                    variables: {
                        input: {
                            eventIdList: newEventIds,
                            isAuth: false
                        }
                    },
                    fetchPolicy: "network-only",
                });

                const locationCertInfo = data?.traceLocationCertificates?.locationCertInfo || [];

                const newCertificates: LocationCertificatesMap = {};
                locationCertInfo.forEach((info: { eventId: string; locationCertificates: Certificate[] }) => {
                    newCertificates[info.eventId] = info.locationCertificates || [];
                    fetchedEventIds.current.add(info.eventId);
                });

                newEventIds.forEach(id => {
                    if (!newCertificates.hasOwnProperty(id)) {
                        newCertificates[id] = [];
                        fetchedEventIds.current.add(id);
                    }
                });

                setLocationCertificates(prev => ({
                    ...prev,
                    ...newCertificates
                }));

                success = true;
            } catch (error) {
                attempt++;
                if (attempt >= maxRetries) {
                    newEventIds.forEach(id => {
                        fetchedEventIds.current.add(id);
                    });
                }
            }
        }

        newEventIds.forEach(id => pendingEventIds.current.delete(id));
        setLoading(false);
    }, [client]);

    /**
     * Helper to get certificates for a single eventId.
     * Returns the array directly, or an empty array if not found.
     */
    const getCertificatesForEvent = useCallback((eventId: string): Certificate[] => {
        return locationCertificates[eventId] || [];
    }, [locationCertificates]);

    return {
        locationCertificates,
        loading,
        fetchLocationCertificates,
        getCertificatesForEvent
    };
}
